import React, { useState, useEffect } from "react";
import { getRepresentatives, updateRepresentative } from "../services/auth";

const PratinidhiManagment = () => {
  const [representatives, setRepresentatives] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    pName: "",
    mob: "",
    address: "",
    proPic: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRepresentatives();
        setRepresentatives(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEditClick = (rep) => {
    setEditingId(rep.ID);
    setFormData({
      pName: rep.pName || "",
      mob: rep.mob || "",
      address: rep.address || "",
      proPic: rep.proPic || "",
    });
    setImagePreview(rep.proPic || null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ pName: "", mob: "", address: "", proPic: "" });
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();

      // Add all the fields to formData
      formDataToSend.append(
        "pratinidhiData",
        JSON.stringify({
          ID: editingId,
          pName: formData.pName,
          mob: formData.mob,
          address: formData.address,
          // Don't include proPic here as we'll handle it separately
        })
      );

      // Only append the file if it's a new file
      if (formData.proPic instanceof File) {
        formDataToSend.append("proPic", formData.proPic);
      }

      const updatedRep = await updateRepresentative(formDataToSend);

      // Update the representatives list
      setRepresentatives((prev) =>
        prev.map((rep) =>
          rep.ID === editingId
            ? {
                ...rep,
                ...updatedRep.data,
                proPic: updatedRep.data.proPic || rep.proPic,
              }
            : rep
        )
      );

      handleCancelEdit();
      alert("Representative updated successfully!");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  // Update the handleImageChange function
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setFormData((prev) => ({ ...prev, proPic: file }));

      // Clean up the URL when component unmounts or when image changes
      return () => URL.revokeObjectURL(previewUrl);
    }
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-flex items-center px-4 py-2 bg-indigo-100 rounded-lg">
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="text-indigo-600 font-medium">Loading...</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="p-6 text-center bg-red-50 rounded-lg border border-red-200">
        <div className="text-red-600 inline-flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Error: {error}
        </div>
      </div>
    );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          પ્રતિનિધિ મેનેજમેન્ટ
        </h1>
        <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg text-sm font-medium">
          Total: {representatives.length} પ્રતિનિધિ
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {["ફોટો", "નામ", "ગામ", "મોબાઈલ", "એડ્રેસ", "એડિટ"].map(
                  (head) => (
                    <th
                      key={head}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {head}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {representatives.map((rep) => {
                const isEditing = editingId === rep.ID;

                return (
                  <tr
                    key={rep.ID}
                    className={isEditing ? "bg-indigo-50" : "hover:bg-gray-50"}
                  >
                    {isEditing ? (
                      <>
                        {/* When in Edit Mode - Show a single row with all fields aligned */}
                        <td colSpan="6" className="px-6 py-4">
                          <div className="grid grid-cols-12 gap-4 items-start">
                            {/* Photo with image upload */}
                            <div className="col-span-2">
                              <div className="flex items-center space-y-2 flex-col">
                                {imagePreview ? (
                                  <div className="relative">
                                    <img
                                      src={imagePreview}
                                      alt="Preview"
                                      className="h-16 w-16 rounded-full object-cover border-2 border-indigo-200"
                                    />
                                    <div className="absolute -top-1 -right-1 bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                      ✓
                                    </div>
                                  </div>
                                ) : rep.proPic ? (
                                  <img
                                    src={rep.proPic}
                                    alt={rep.pName}
                                    className="h-16 w-16 rounded-full object-cover border-2 border-white shadow"
                                  />
                                ) : (
                                  <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 font-medium shadow">
                                    {rep.pName?.charAt(0)?.toUpperCase() || "P"}
                                  </div>
                                )}

                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                  className="w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                />
                              </div>
                            </div>

                            {/* Name */}
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Full Name
                              </label>
                              <input
                                type="text"
                                name="pName"
                                value={formData.pName}
                                onChange={handleInputChange}
                                placeholder="Full Name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                              />
                            </div>

                            {/* Village - Not editable */}
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Village
                              </label>
                              <div className="h-10 flex items-center">
                                <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded-md text-sm">
                                  {rep.village}
                                </span>
                              </div>
                            </div>

                            {/* Mobile */}
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Mobile Number
                              </label>
                              <input
                                type="text"
                                name="mob"
                                value={formData.mob}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                              />
                            </div>

                            {/* Address - Two columns wide */}
                            <div className="col-span-4">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Address
                              </label>
                              <div>
                                <textarea
                                  name="address"
                                  value={formData.address}
                                  onChange={handleInputChange}
                                  rows="2"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                                  placeholder="Address"
                                />
                                <div className="mt-2 flex justify-end gap-2">
                                  <button
                                    onClick={handleSubmit}
                                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition flex items-center text-sm font-medium shadow-sm"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4 mr-1"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition flex items-center text-sm font-medium shadow-sm"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4 mr-1"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        {/* Regular View Mode - Normal table cells */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {rep.proPic ? (
                              <img
                                src={rep.proPic}
                                alt={rep.pName}
                                className="h-12 w-12 rounded-full object-cover border-2 border-white shadow"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 font-medium shadow">
                                {rep.pName?.charAt(0)?.toUpperCase() || "P"}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-gray-900 font-medium">
                            {rep.pName}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded-md text-sm">
                            {rep.village}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-600 inline-flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            {rep.mob}
                          </span>
                        </td>

                        <td className="px-6 py-4">
                          <span className="text-gray-600 text-sm">
                            {rep.address || ""}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleEditClick(rep)}
                            className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md transition inline-flex items-center text-sm font-medium"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Edit
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {representatives.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12 mx-auto text-gray-300 mb-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                      <p className="text-lg font-medium">
                        No representatives found
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Representatives will appear here once added
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PratinidhiManagment;
